import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  LogOut,
  RefreshCw,
  Search,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Globe,
  Wrench,
  Trash2,
  Edit,
  Eye,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Car,
  Briefcase,
  Star
} from 'lucide-react';

import {
  verifyAdminSession,
  adminLogout,
  fetchAdminStats,
  fetchQuotes,
  updateQuote as apiUpdateQuote,
  deleteQuote as apiDeleteQuote,
  fetchApplicationStats,
  fetchApplications,
  updateApplication as apiUpdateApplication,
  deleteApplication as apiDeleteApplication
} from '../utils/adminApi';

// ============ CONSTANTS ============
const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-brand-grey text-brand-navy',
  assigned: 'bg-purple-100 text-purple-700',
  quoted: 'bg-orange-100 text-orange-700',
  booked: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-brand-slate',
  pending: 'bg-blue-100 text-blue-700',
  reviewing: 'bg-brand-grey text-brand-navy',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

const TRADE_ICONS = {
  handyman: '🔧',
  carpenter: '🪚',
  painter: '🎨',
  plumber: '🔩',
  electrician: '⚡',
  plasterer: '🧱',
  cleaner: '✨',
  general: '🏠'
};

const QUOTE_STATUSES = ['new', 'contacted', 'assigned', 'quoted', 'booked', 'completed', 'closed'];
const APP_STATUSES = ['pending', 'reviewing', 'approved', 'rejected'];

// ============ HELPER COMPONENTS ============
const StatCard = ({ title, value, icon: Icon, color = 'brand-navy' }) => (
  <Card>
    <CardContent className="p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-brand-slate">{title}</p>
          <p className={`text-2xl sm:text-3xl font-bold text-${color}`}>{value}</p>
        </div>
        <div className={`w-12 h-12 bg-${color}/10 rounded-full flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => (
  <Badge className={STATUS_COLORS[status] || 'bg-slate-100 text-slate-700'}>
    {status}
  </Badge>
);

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============ LEADS PANEL ============
const LeadsPanel = ({ 
  quotes, 
  stats, 
  filters, 
  setFilters, 
  onRefresh, 
  onView, 
  onEdit, 
  onDelete,
  loading 
}) => {
  const filteredQuotes = quotes.filter(quote => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        quote.name?.toLowerCase().includes(searchLower) ||
        quote.email?.toLowerCase().includes(searchLower) ||
        quote.phone?.includes(filters.search) ||
        quote.job_description?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard title="Total Leads" value={stats.total} icon={Users} />
          <StatCard title="New" value={stats.by_status?.new || 0} icon={AlertCircle} color="blue-600" />
          <StatCard title="In Progress" value={(stats.by_status?.contacted || 0) + (stats.by_status?.assigned || 0)} icon={Clock} color="orange-600" />
          <StatCard title="Completed" value={stats.by_status?.completed || 0} icon={CheckCircle} color="green-600" />
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search leads..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {QUOTE_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.trade_type || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, trade_type: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Trade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Trades</SelectItem>
                {Object.keys(TRADE_ICONS).map(t => (
                  <SelectItem key={t} value={t}>{TRADE_ICONS[t]} {t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Trade</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuotes.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(quote.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{quote.name}</div>
                      <div className="text-sm text-brand-slate">{quote.email}</div>
                    </TableCell>
                    <TableCell>
                      <span className="text-lg mr-1">{TRADE_ICONS[quote.trade_type] || '🏠'}</span>
                      {quote.trade_type}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={quote.status} />
                    </TableCell>
                    <TableCell className="text-sm text-brand-slate">
                      {quote.source_page || '/'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => onView(quote)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onEdit(quote)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onDelete(quote.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredQuotes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-brand-slate">
                      No leads found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ APPLICATIONS PANEL ============
const ApplicationsPanel = ({ 
  applications, 
  stats, 
  filters, 
  setFilters, 
  onRefresh, 
  onView, 
  onEdit, 
  onDelete,
  loading 
}) => {
  const filteredApps = applications.filter(app => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        app.full_name?.toLowerCase().includes(searchLower) ||
        app.email?.toLowerCase().includes(searchLower) ||
        app.phone?.includes(filters.search);
      if (!matchesSearch) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard title="Total Applications" value={stats.total} icon={Briefcase} />
          <StatCard title="Pending" value={stats.by_status?.pending || 0} icon={Clock} color="blue-600" />
          <StatCard title="Reviewing" value={stats.by_status?.reviewing || 0} icon={Eye} color="orange-600" />
          <StatCard title="Approved" value={stats.by_status?.approved || 0} icon={CheckCircle} color="green-600" />
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search applications..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {APP_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Skills</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApps.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(app.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{app.full_name}</div>
                      <div className="text-sm text-brand-slate">{app.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(app.skills || []).slice(0, 3).map((skill, i) => (
                          <Badge key={`${app.id}-skill-${skill}`} variant="outline" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {(app.skills || []).length > 3 && (
                          <Badge variant="outline" className="text-xs">+{app.skills.length - 3}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={app.status} />
                    </TableCell>
                    <TableCell>{app.years_experience} years</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => onView(app)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onEdit(app)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onDelete(app.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredApps.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-brand-slate">
                      No applications found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ LEAD DETAIL DIALOG ============
const LeadDetailDialog = ({ quote, open, onClose, onUpdate, editMode, setEditMode }) => {
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (quote) {
      setEditData({
        status: quote.status,
        admin_notes: quote.admin_notes || ''
      });
    }
  }, [quote]);

  if (!quote) return null;

  const handleSave = () => {
    onUpdate(quote.id, editData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Lead Details</span>
            <StatusBadge status={quote.status} />
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-brand-slate" />
              <span className="font-medium">{quote.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-brand-slate" />
              <a href={`mailto:${quote.email}`} className="text-brand-yellow hover:underline">{quote.email}</a>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-brand-slate" />
              <a href={`tel:${quote.phone}`} className="text-brand-yellow hover:underline">{quote.phone}</a>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-brand-slate" />
              <span>{quote.properties_managed || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-brand-slate" />
              <span>{formatDate(quote.created_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-brand-slate" />
              <span>{quote.source_page || '/'}</span>
            </div>
          </div>

          {/* Job Description */}
          <div>
            <h4 className="font-medium mb-2">Job Description</h4>
            <p className="text-brand-slate bg-slate-50 p-3 rounded-lg">{quote.job_description}</p>
          </div>

          {/* Edit Mode */}
          {editMode ? (
            <div className="space-y-4 border-t pt-4">
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={editData.status} onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUOTE_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Admin Notes</label>
                <Textarea
                  value={editData.admin_notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, admin_notes: e.target.value }))}
                  placeholder="Add internal notes..."
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} className="bg-brand-yellow text-brand-navy hover:bg-yellow-300">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              {quote.admin_notes && (
                <div>
                  <h4 className="font-medium mb-2">Admin Notes</h4>
                  <p className="text-brand-slate bg-slate-50 p-3 rounded-lg">{quote.admin_notes}</p>
                </div>
              )}
              <Button variant="outline" onClick={() => setEditMode(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Lead
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ APPLICATION DETAIL DIALOG ============
const ApplicationDetailDialog = ({ app, open, onClose, onUpdate, editMode, setEditMode }) => {
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (app) {
      setEditData({
        status: app.status,
        admin_notes: app.admin_notes || ''
      });
    }
  }, [app]);

  if (!app) return null;

  const handleSave = () => {
    onUpdate(app.id, editData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Application Details</span>
            <StatusBadge status={app.status} />
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-brand-slate" />
              <span className="font-medium">{app.full_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-brand-slate" />
              <a href={`mailto:${app.email}`} className="text-brand-yellow hover:underline">{app.email}</a>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-brand-slate" />
              <a href={`tel:${app.phone}`} className="text-brand-yellow hover:underline">{app.phone}</a>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-brand-slate" />
              <span>{app.postcode || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-brand-slate" />
              <span>{app.years_experience} years experience</span>
            </div>
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-brand-slate" />
              <span>{app.has_transport ? 'Has transport' : 'No transport'}</span>
            </div>
          </div>

          {/* Skills */}
          <div>
            <h4 className="font-medium mb-2">Skills</h4>
            <div className="flex flex-wrap gap-2">
              {(app.skills || []).map((skill) => (
                <Badge key={`${app.id}-detail-${skill}`} variant="outline">{skill}</Badge>
              ))}
            </div>
          </div>

          {/* Availability */}
          <div>
            <h4 className="font-medium mb-2">Availability</h4>
            <div className="flex flex-wrap gap-2">
              {(app.availability || []).map((day) => (
                <Badge key={`${app.id}-avail-${day}`} className="bg-brand-grey text-brand-navy">{day}</Badge>
              ))}
            </div>
          </div>

          {/* Documents */}
          {app.cv_url && (
            <div>
              <h4 className="font-medium mb-2">CV</h4>
              <a href={app.cv_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-brand-yellow hover:underline">
                <FileText className="w-4 h-4" />
                View CV
              </a>
            </div>
          )}

          {/* Work Photos */}
          {app.work_photos && app.work_photos.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Work Photos</h4>
              <div className="grid grid-cols-3 gap-2">
                {app.work_photos.map((photo, i) => (
                  <a key={`${app.id}-photo-${i}`} href={photo} target="_blank" rel="noopener noreferrer">
                    <img src={photo} alt={`Work sample ${i + 1}`} className="w-full h-24 object-cover rounded-lg" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Edit Mode */}
          {editMode ? (
            <div className="space-y-4 border-t pt-4">
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={editData.status} onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APP_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Admin Notes</label>
                <Textarea
                  value={editData.admin_notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, admin_notes: e.target.value }))}
                  placeholder="Add internal notes..."
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} className="bg-brand-yellow text-brand-navy hover:bg-yellow-300">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              {app.admin_notes && (
                <div>
                  <h4 className="font-medium mb-2">Admin Notes</h4>
                  <p className="text-brand-slate bg-slate-50 p-3 rounded-lg">{app.admin_notes}</p>
                </div>
              )}
              <Button variant="outline" onClick={() => setEditMode(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Application
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN ADMIN DASHBOARD ============
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads');
  const [quotes, setQuotes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [appStats, setAppStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [filters, setFilters] = useState({ status: '', trade_type: '', search: '' });
  const [appFilters, setAppFilters] = useState({ status: '', search: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, quotesData, appStatsData, appsData] = await Promise.all([
        fetchAdminStats().catch(() => null),
        fetchQuotes(filters).catch(() => ({ quotes: [] })),
        fetchApplicationStats().catch(() => null),
        fetchApplications(appFilters).catch(() => ({ applications: [] }))
      ]);

      setStats(statsData);
      setQuotes(quotesData.quotes || []);
      setAppStats(appStatsData);
      setApplications(appsData.applications || []);
    } catch (error) {
      if (error.message?.includes('401') || error.message?.includes('403')) {
        navigate('/admin-login');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, appFilters, navigate]);

  // Verify session on mount
  useEffect(() => {
    const checkSession = async () => {
      const isValid = await verifyAdminSession();
      if (!isValid) {
        navigate('/admin-login');
        return;
      }
      fetchData();
    };
    checkSession();
  }, [navigate, fetchData]);

  // Refetch when filters change
  useEffect(() => {
    fetchData();
  }, [filters.status, filters.trade_type, appFilters.status, fetchData]);

  const handleLogout = async () => {
    await adminLogout();
    navigate('/admin-login');
  };

  const handleUpdateQuote = async (quoteId, updates) => {
    try {
      await apiUpdateQuote(quoteId, updates);
      fetchData();
      setEditMode(false);
    } catch (error) {
      // Display error to admin - operation failed
      alert('Failed to update lead. Please try again.');
      if (process.env.NODE_ENV === 'development') {
        console.error('Update quote error:', error.message);
      }
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await apiDeleteQuote(quoteId);
      fetchData();
      setSelectedQuote(null);
    } catch (error) {
      alert('Failed to delete lead. Please try again.');
      if (process.env.NODE_ENV === 'development') {
        console.error('Delete quote error:', error.message);
      }
    }
  };

  const handleUpdateApplication = async (appId, updates) => {
    try {
      await apiUpdateApplication(appId, updates);
      fetchData();
      setEditMode(false);
    } catch (error) {
      alert('Failed to update application. Please try again.');
      if (process.env.NODE_ENV === 'development') {
        console.error('Update application error:', error.message);
      }
    }
  };

  const handleDeleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await apiDeleteApplication(appId);
      fetchData();
      setSelectedApplication(null);
    } catch (error) {
      alert('Failed to delete application. Please try again.');
      if (process.env.NODE_ENV === 'development') {
        console.error('Delete application error:', error.message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-brand-grey">
      {/* Header */}
      <header className="bg-brand-navy text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <span className="text-sm text-slate-400">Expert Property Maintenance</span>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-white hover:text-brand-yellow">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('leads')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'leads' 
                  ? 'border-brand-yellow text-brand-navy' 
                  : 'border-transparent text-brand-slate hover:text-brand-navy'
              }`}
            >
              Leads ({stats?.total || 0})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'applications' 
                  ? 'border-brand-yellow text-brand-navy' 
                  : 'border-transparent text-brand-slate hover:text-brand-navy'
              }`}
            >
              Applications ({appStats?.total || 0})
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'leads' ? (
          <LeadsPanel
            quotes={quotes}
            stats={stats}
            filters={filters}
            setFilters={setFilters}
            onRefresh={fetchData}
            onView={(q) => { setSelectedQuote(q); setEditMode(false); }}
            onEdit={(q) => { setSelectedQuote(q); setEditMode(true); }}
            onDelete={handleDeleteQuote}
            loading={loading}
          />
        ) : (
          <ApplicationsPanel
            applications={applications}
            stats={appStats}
            filters={appFilters}
            setFilters={setAppFilters}
            onRefresh={fetchData}
            onView={(a) => { setSelectedApplication(a); setEditMode(false); }}
            onEdit={(a) => { setSelectedApplication(a); setEditMode(true); }}
            onDelete={handleDeleteApplication}
            loading={loading}
          />
        )}
      </main>

      {/* Detail Dialogs */}
      <LeadDetailDialog
        quote={selectedQuote}
        open={!!selectedQuote}
        onClose={() => setSelectedQuote(null)}
        onUpdate={handleUpdateQuote}
        editMode={editMode}
        setEditMode={setEditMode}
      />
      <ApplicationDetailDialog
        app={selectedApplication}
        open={!!selectedApplication}
        onClose={() => setSelectedApplication(null)}
        onUpdate={handleUpdateApplication}
        editMode={editMode}
        setEditMode={setEditMode}
      />
    </div>
  );
};

export default AdminDashboard;
